import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import GettingStarted from '@/components/marketing/GettingStarted';
import { ER_REDUCTION, contactHref } from '@/components/marketing/siteFacts';
import {
  ArrowRight,
  Building2,
  CheckCircle,
  TrendingDown,
  Users,
  BarChart3,
  Target,
  Brain,
  Radio,
  Clock,
  Shield,
  DollarSign,
  Activity,
  AlertTriangle,
  Zap,
  PieChart,
  ArrowLeft
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

interface NetworkStats {
  totalProviders: number;
  totalOrganizations: number;
  statesCovered: number;
}

// WeaveIn Brand Colors
const BRAND = {
  purple: 'var(--v2-ink)',
  purpleDark: 'var(--v2-ink)',
  teal: 'var(--v2-accent)',
};

/**
 * Employer Solutions Page
 * Detailed value proposition for self-insured employers
 */
export default function EmployerSolutionsPage() {
  usePageMeta(SEO_ROUTES['/solutions/employers']);

  const currentYear = new Date().getFullYear();
  const [stats, setStats] = useState<NetworkStats | null>(null);

  useEffect(() => {
    fetch('/provider-api/stats')
      .then(res => res.json())
      .then(data => {
        if (data.total_providers) {
          setStats({
            totalProviders: data.total_providers,
            totalOrganizations: Object.keys(data.by_organization_type || {}).length,
            statesCovered: Object.keys(data.by_state || {}).length,
          });
        }
      })
      .catch(() => {
        fetch(`${API_BASE}/api/public/network-stats`)
          .then(res => res.json())
          .then(data => setStats(data))
          .catch(() => setStats(null));
      });
  }, []);

  const providerCount = stats
    ? (stats.totalProviders >= 1000000 ? (stats.totalProviders / 1000000).toFixed(1) + 'M+' : Math.floor(stats.totalProviders / 1000).toLocaleString() + 'K+')
    : '1.7M+';

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="bg-paper border-b border-v2border py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 bg-v2accent-soft rounded-full mb-6">
              <Building2 className="w-5 h-5 text-v2accent mr-2" />
              <span className="text-ink font-medium">For Employers</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-ink mb-6">
              Stop Adding Point Solutions.<br />
              <span className="text-v2accent">Start Addressing Root Causes.</span>
            </h1>
            <p className="text-xl text-ink-500 mb-8">
              Self-insured employers spend millions on benefits with no visibility into what works.
              WeaveIn connects the dots between workforce data and health outcomes—so you can finally
              see what's driving costs and fix it.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to={contactHref('employer')}
                className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors"
              >
                Talk to us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/roi-calculator"
                className="inline-flex items-center px-8 py-4 bg-v2accent-soft text-ink text-lg font-semibold rounded-lg hover:bg-ink/20 transition-colors"
              >
                Try the ROI calculator
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              The Hidden Costs You're Missing
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Traditional benefits strategies treat symptoms. WeaveIn addresses the social determinants
              that actually drive 80% of health outcomes.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center mb-6">
                <DollarSign className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-ink dark:text-white mb-3">$25,572</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Average family premium. You absorb 73% of this cost—and it rises 8-12% annually.
              </p>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Each 1% trend = $2.5M+ for a 10K employee company
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center mb-6">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-ink dark:text-white mb-3">&lt;20%</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Benefits utilization rate. Your employees don't use the programs you pay for—because they don't know they exist.
              </p>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                EAP utilization averages just 4%
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="w-12 h-12 bg-v2accent-soft rounded-xl flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-v2accent-ink" />
              </div>
              <h3 className="text-xl font-bold text-ink dark:text-white mb-3">35%</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Average turnover rate in hourly workforce. Most employers never see the SDOH factors driving this churn.
              </p>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Cost to replace: 50-200% of annual salary
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              How WeaveIn Works for Employers
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Three steps to transform reactive benefits into proactive workforce support.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="relative">
              <div className="bg-v2accent rounded-2xl p-8 text-white h-full">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <div className="text-v2accent text-sm font-bold mb-2">STEP 1</div>
                <h3 className="text-2xl font-bold mb-4">Signal Detection</h3>
                <p className="text-gray-300 mb-6">
                  Connect your HRIS, attendance, and claims data. Our AI identifies at-risk workers
                  before problems cascade—detecting patterns humans miss.
                </p>
                <ul className="space-y-3 text-sm text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-v2accent mr-2 mt-0.5 flex-shrink-0" />
                    HRIS integration (Workday, ADP, etc.)
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-v2accent mr-2 mt-0.5 flex-shrink-0" />
                    Attendance pattern analysis
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-v2accent mr-2 mt-0.5 flex-shrink-0" />
                    Claims data correlation
                  </li>
                </ul>
              </div>
            </div>

            <div className="relative">
              <div className="bg-v2accent rounded-2xl p-8 text-white h-full">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6">
                  <Brain className="w-7 h-7 text-white" />
                </div>
                <div className="text-v2accent text-sm font-bold mb-2">STEP 2</div>
                <h3 className="text-2xl font-bold mb-4">Program Intelligence</h3>
                <p className="text-gray-300 mb-6">
                  Match workers to bundled programs from our {providerCount} provider network—including
                  benefits they didn't know existed in your current plans.
                </p>
                <ul className="space-y-3 text-sm text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-v2accent mr-2 mt-0.5 flex-shrink-0" />
                    Average 2-6 programs per worker
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-v2accent mr-2 mt-0.5 flex-shrink-0" />
                    SDOH + existing benefits bundling
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-v2accent mr-2 mt-0.5 flex-shrink-0" />
                    Community resource matching
                  </li>
                </ul>
              </div>
            </div>

            <div className="relative">
              <div className="bg-v2accent rounded-2xl p-8 text-white h-full">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6">
                  <Radio className="w-7 h-7 text-white" />
                </div>
                <div className="text-v2accent text-sm font-bold mb-2">STEP 3</div>
                <h3 className="text-2xl font-bold mb-4">Closed-Loop Tracking</h3>
                <p className="text-gray-300 mb-6">
                  Omnichannel outreach in 110+ languages. Track from referral to enrollment to
                  outcome. Finally see what works—and prove ROI.
                </p>
                <ul className="space-y-3 text-sm text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-v2accent mr-2 mt-0.5 flex-shrink-0" />
                    SMS, email, app notifications
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-v2accent mr-2 mt-0.5 flex-shrink-0" />
                    Real-time enrollment tracking
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-v2accent mr-2 mt-0.5 flex-shrink-0" />
                    Outcome measurement & reporting
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              Real Problems We Solve
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="flex items-start mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-4 text-xl">
                  🏠
                </div>
                <div>
                  <h3 className="text-lg font-bold text-ink dark:text-white">Housing Instability</h3>
                  <p className="text-sm text-gray-500">→ Missed medications → ER visits</p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Maria's rent increased 40%. She cut back on diabetes medication to pay rent.
                Six months later, she's in the ER with complications.
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <div className="text-green-700 dark:text-green-400 text-sm font-semibold mb-1">WeaveIn Solution:</div>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Detect pattern of increased absenteeism + pharmacy claims gaps. Connect Maria
                  to rental assistance + medication copay programs before the ER visit.
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="flex items-start mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-4 text-xl">
                  👶
                </div>
                <div>
                  <h3 className="text-lg font-bold text-ink dark:text-white">Caregiving Burden</h3>
                  <p className="text-sm text-gray-500">→ Burnout → Turnover</p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                James is caring for his aging mother while raising two kids. He's missed 15 days
                in 3 months. His manager thinks he's disengaged.
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <div className="text-green-700 dark:text-green-400 text-sm font-semibold mb-1">WeaveIn Solution:</div>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Identify caregiving signals through attendance patterns. Connect James to
                  respite care, elder support, and flexible work arrangements.
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="flex items-start mb-4">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 text-xl">
                  🍎
                </div>
                <div>
                  <h3 className="text-lg font-bold text-ink dark:text-white">Food Insecurity</h3>
                  <p className="text-sm text-gray-500">→ Chronic disease flare-ups</p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Sarah's family is "food insecure" but not quite poor enough for SNAP.
                Her hypertension medication works—but not when she can't afford healthy food.
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <div className="text-green-700 dark:text-green-400 text-sm font-semibold mb-1">WeaveIn Solution:</div>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Match Sarah to food bank programs, produce prescription programs, and
                  nutrition counseling—bundled with her existing benefits.
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="flex items-start mb-4">
                <div className="w-10 h-10 bg-v2accent-soft rounded-lg flex items-center justify-center mr-4 text-xl">
                  💳
                </div>
                <div>
                  <h3 className="text-lg font-bold text-ink dark:text-white">Financial Stress</h3>
                  <p className="text-sm text-gray-500">→ Anxiety → Presenteeism</p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Tom has $400 in unexpected car repairs. He's physically at work but mentally
                somewhere else—costing you productivity you can't measure.
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <div className="text-green-700 dark:text-green-400 text-sm font-semibold mb-1">WeaveIn Solution:</div>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Connect Tom to emergency assistance funds, financial coaching, and your
                  company's EAP—proactively, before he asks.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-ink dark:text-white mb-6">
                What a pilot measures, in 90 days
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                The results below come from our Medi-Cal work, not yet from employer
                populations. An employer pilot measures each one against your own baseline,
                and reports it within one quarter.
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <TrendingDown className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-ink dark:text-white">{ER_REDUCTION.value} {ER_REDUCTION.label}</h4>
                    <p className="text-gray-600 dark:text-gray-300">{ER_REDUCTION.definition} Measured in our Medi-Cal cohort; not yet in employer populations.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-ink dark:text-white">Pilot target: turnover among at-risk workers</h4>
                    <p className="text-gray-600 dark:text-gray-300">Measured against your own turnover baseline for the workers the program reaches</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-v2accent-soft rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Activity className="w-6 h-6 text-v2accent-ink" />
                  </div>
                  <div>
                    <h4 className="font-bold text-ink dark:text-white">Pilot target: benefits utilization</h4>
                    <p className="text-gray-600 dark:text-gray-300">How many more workers use the programs you already pay for</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-v2accent-soft rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <PieChart className="w-6 h-6 text-v2accent" />
                  </div>
                  <div>
                    <h4 className="font-bold text-ink dark:text-white">Pilot target: return on program cost</h4>
                    <p className="text-gray-600 dark:text-gray-300">Model it with your own assumptions in the ROI calculator before you start</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-v2accent rounded-2xl p-8 text-white">
              <div className="flex items-center mb-8">
                <Clock className="w-6 h-6 text-v2accent mr-3" />
                <h3 className="text-xl font-bold">TIME TO VALUE</h3>
              </div>
              <div className="space-y-6">
                <div className="flex justify-between items-center py-4 border-b border-white/20">
                  <div>
                    <div className="text-gray-300">Integration and setup</div>
                    <div className="text-sm text-gray-400">HRIS, claims, attendance data</div>
                  </div>
                  <span className="text-v2accent font-bold text-xl">30 days</span>
                </div>
                <div className="flex justify-between items-center py-4 border-b border-white/20">
                  <div>
                    <div className="text-gray-300">First at-risk workers identified</div>
                    <div className="text-sm text-gray-400">AI detection begins</div>
                  </div>
                  <span className="text-v2accent font-bold text-xl">Week 1</span>
                </div>
                <div className="flex justify-between items-center py-4 border-b border-white/20">
                  <div>
                    <div className="text-gray-300">First enrollments completed</div>
                    <div className="text-sm text-gray-400">Workers connected to programs</div>
                  </div>
                  <span className="text-v2accent font-bold text-xl">Month 2</span>
                </div>
                <div className="flex justify-between items-center py-4">
                  <div>
                    <div className="text-gray-300">First pilot readout</div>
                    <div className="text-sm text-gray-400">Against your own baseline</div>
                  </div>
                  <span className="text-v2accent font-bold text-xl">90 days</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal Customer */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              Is WeaveIn Right for You?
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              WeaveIn works best for self-insured employers with significant hourly workforce populations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-ink dark:text-white mb-2">1,000-50,000</div>
              <div className="text-gray-600 dark:text-gray-400">Lives in your plan</div>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-ink dark:text-white mb-2">Self-Insured</div>
              <div className="text-gray-600 dark:text-gray-400">Full cost visibility</div>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-ink dark:text-white mb-2">High Hourly</div>
              <div className="text-gray-600 dark:text-gray-400">Workforce composition</div>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-ink dark:text-white mb-2">Multi-Site</div>
              <div className="text-gray-600 dark:text-gray-400">Distributed locations</div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Industries we serve: <span className="font-semibold">Retail, Hospitality, Logistics, Healthcare, Manufacturing</span>
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-paper border-b border-v2border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-ink mb-6">
            Ready to See What's Really Driving Your Costs?
          </h2>
          <p className="text-xl text-ink-500 mb-10">
            Schedule a demo to see how WeaveIn can reduce your healthcare costs while improving employee retention.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <Link
              to={contactHref('employer')}
              className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors shadow-lg"
            >
              Talk to us
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to={contactHref('guide', 'Employer guide')}
              className="inline-flex items-center px-8 py-4 bg-transparent text-ink text-lg font-semibold rounded-lg border-2 border-ink hover:bg-ink hover:text-paper transition-colors"
            >
              Request the employer guide
            </Link>
          </div>
        </div>
      </section>

      <GettingStarted />

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
}
